import {
  useState, useMemo, useRef, useEffect,
  ChangeEvent, MouseEvent, ReactNode,
} from "react";
import { fetchQuotes, deleteQuote, QuoteRecord } from "../../services/quoteService";

// ─── Types ────────────────────────────────────────────────────────────────────

type SortField = "created_at" | "company" | "name" | "email";
type SortDir   = "asc" | "desc";

interface ToastState    { visible: boolean; message: string; type: "success" | "error" | "info" }
interface ViewModal     { open: boolean; quote: QuoteRecord | null }
interface DeleteModal   { open: boolean; quote: QuoteRecord | null; deleting: boolean }

// ─── Helpers ──────────────────────────────────────────────────────────────────

const IST = "Asia/Kolkata";

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    timeZone: IST,
  });
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: IST,
  });
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

function isThisWeek(iso: string): boolean {
  return daysSince(iso) <= 7;
}

function highlight(text: string, q: string): ReactNode {
  if (!q.trim()) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "rgba(191,61,12,0.3)", color: "var(--or)", borderRadius: 2, padding: "0 1px" }}>
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function exportCSV(rows: QuoteRecord[]) {
  const headers = ["ID", "Name", "Company", "Email", "Phone", "Enzyme", "City", "Country", "Message", "Submitted"];
  const lines = rows.map((r) => [
    r.id,
    `"${r.name}"`,
    `"${r.company}"`,
    `"${r.email}"`,
    `"${r.phone ?? ""}"`,
    `"${r.enzyme ?? ""}"`,
    `"${r.city ?? ""}"`,
    `"${r.country ?? ""}"`,
    `"${(r.message ?? "").replace(/"/g, '""')}"`,
    `"${fmtDate(r.created_at)}"`,
  ].join(","));
  const csv = [headers.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `quote-requests-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Atoms ────────────────────────────────────────────────────────────────────

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 1, marginLeft: 4, opacity: active ? 1 : 0.3 }}>
      <span style={{ fontSize: 8, lineHeight: 1, color: active && dir === "asc"  ? "var(--or)" : "var(--text3)" }}>▲</span>
      <span style={{ fontSize: 8, lineHeight: 1, color: active && dir === "desc" ? "var(--or)" : "var(--text3)" }}>▼</span>
    </span>
  );
}

function IconBtn({
  title, onClick, children, danger, accent,
}: { title: string; onClick: () => void; children: ReactNode; danger?: boolean; accent?: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        width: 30, height: 30, borderRadius: 7,
        border: `1px solid ${hov ? (danger ? "rgba(224,82,82,0.4)" : accent ? "rgba(191,61,12,0.4)" : "rgba(255,255,255,0.14)") : "var(--border)"}`,
        background: hov ? (danger ? "rgba(224,82,82,0.10)" : accent ? "var(--or-dim)" : "rgba(255,255,255,0.05)") : "transparent",
        color: hov ? (danger ? "#e05252" : accent ? "var(--or)" : "var(--text2)") : "var(--text3)",
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s", flexShrink: 0,
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {children}
    </button>
  );
}

function Pill({ value }: { value: string | null }) {
  if (!value) return <span style={{ color: "var(--text3)", fontSize: 12 }}>—</span>;
  return (
    <span style={{
      fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 500,
      background: "var(--or-dim)", border: "1px solid rgba(191,61,12,0.22)",
      color: "var(--or)", whiteSpace: "nowrap", maxWidth: 140,
      overflow: "hidden", textOverflow: "ellipsis", display: "inline-block",
    }}>
      {value}
    </span>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 40 }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid var(--border)", borderTopColor: "var(--or)", animation: "spin 0.7s linear infinite" }} />
      <span style={{ fontSize: 14, color: "var(--text3)" }}>Loading quote requests…</span>
    </div>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 40 }}>
      <div style={{ background: "rgba(224,82,82,0.08)", border: "1px solid rgba(224,82,82,0.22)", borderRadius: 14, padding: "28px 36px", textAlign: "center", maxWidth: 420 }}>
        <div style={{ fontSize: 24, marginBottom: 10 }}>⚠</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#e05252", marginBottom: 6 }}>Failed to load quotes</div>
        <div style={{ fontSize: 13, color: "var(--text3)", marginBottom: 20, lineHeight: 1.6 }}>{message}</div>
        <button
          onClick={onRetry}
          style={{
            padding: "9px 22px", borderRadius: 9, border: "1px solid rgba(191,61,12,0.35)",
            background: "linear-gradient(135deg,#BF3D0C,#E8593C)", color: "#fff",
            fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
          }}
        >
          Retry
        </button>
      </div>
    </div>
  );
}

// ─── View modal ───────────────────────────────────────────────────────────────

function ViewModal({ modal, onClose }: { modal: ViewModal; onClose: () => void }) {
  if (!modal.open || !modal.quote) return null;
  const q = modal.quote;

  const location = [q.city, q.country].filter(Boolean).join(", ") || "—";
  const metaRows = [
    { label: "Full Name",  value: q.name },
    { label: "Company",    value: q.company },
    { label: "Email",      value: q.email },
    { label: "Phone",      value: q.phone  ?? "—" },
    { label: "Enzyme",     value: q.enzyme ?? "—" },
    { label: "Location",   value: location },
    { label: "IP Address", value: q.ip_address ?? "—" },
    { label: "Submitted",  value: fmtDateTime(q.created_at) },
  ];

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={onClose}
    >
      <div
        onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        style={{
          background: "#1a1917", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16,
          width: 620, maxWidth: "95vw", maxHeight: "90vh", overflow: "hidden",
          display: "flex", flexDirection: "column", animation: "modalIn 0.2s ease",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>Quote Request #{q.id}</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: "var(--text)" }}>{q.name}</div>
            <div style={{ fontSize: 13, color: "var(--text3)", marginTop: 2 }}>{q.company}</div>
          </div>
          <button
            onClick={onClose}
            style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Meta grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {metaRows.map((row) => (
              <div key={row.label} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 4 }}>{row.label}</div>
                <div style={{ fontSize: 13, color: "var(--text2)", wordBreak: "break-all" }}>{row.value}</div>
              </div>
            ))}
          </div>

          {/* Message */}
          {q.message && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>Message</div>
              <div style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.7, background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px", whiteSpace: "pre-wrap" }}>
                {q.message}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
            <a
              href={`mailto:${q.email}?subject=Re: Your Quote Request — Aurel Global&body=Dear ${encodeURIComponent(q.name)},%0A%0A`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: "9px 18px", borderRadius: 9, textDecoration: "none",
                border: "1px solid rgba(191,61,12,0.35)",
                background: "linear-gradient(135deg,#BF3D0C,#E8593C)",
                color: "#fff", fontSize: 13, fontWeight: 500, fontFamily: "'DM Sans',sans-serif",
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              Reply by Email
            </a>
            <button
              onClick={onClose}
              style={{ padding: "9px 18px", borderRadius: 9, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Delete confirm modal ─────────────────────────────────────────────────────

function DeleteConfirmModal({ modal, onConfirm, onCancel }: {
  modal: DeleteModal;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!modal.open || !modal.quote) return null;
  const q = modal.quote;
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 600, background: "rgba(0,0,0,0.78)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={modal.deleting ? undefined : onCancel}
    >
      <div
        onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        style={{ background: "#1a1917", border: "1px solid rgba(224,82,82,0.25)", borderRadius: 14, width: 420, maxWidth: "95vw", padding: "28px 28px 24px", animation: "modalIn 0.18s ease" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "rgba(224,82,82,0.12)", border: "1px solid rgba(224,82,82,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e05252" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>Delete Quote #{q.id}?</div>
            <div style={{ fontSize: 12, color: "var(--text3)" }}>This action cannot be undone.</div>
          </div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: "var(--text2)", fontWeight: 500 }}>{q.name}</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{q.company} · {q.email}</div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            disabled={modal.deleting}
            style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", fontSize: 13, cursor: modal.deleting ? "not-allowed" : "pointer", opacity: modal.deleting ? 0.5 : 1, fontFamily: "'DM Sans',sans-serif" }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={modal.deleting}
            style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(224,82,82,0.4)", background: modal.deleting ? "rgba(224,82,82,0.3)" : "rgba(224,82,82,0.15)", color: "#e05252", fontSize: 13, fontWeight: 500, cursor: modal.deleting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s" }}
          >
            {modal.deleting ? (
              <>
                <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(224,82,82,0.4)", borderTopColor: "#e05252", animation: "spin 0.6s linear infinite" }} />
                Deleting…
              </>
            ) : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const PAGE_SIZES = [10, 20, 50];

const SORT_OPTIONS: { value: string; label: string; field: SortField; dir: SortDir }[] = [
  { value: "newest",    label: "Newest first",  field: "created_at", dir: "desc" },
  { value: "oldest",    label: "Oldest first",  field: "created_at", dir: "asc"  },
  { value: "company",   label: "Company A–Z",   field: "company",    dir: "asc"  },
  { value: "name",      label: "Name A–Z",      field: "name",       dir: "asc"  },
];

export default function QuoteRequests() {
  const [quotes,    setQuotes]    = useState<QuoteRecord[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [search,    setSearch]    = useState("");
  const [sortKey,   setSortKey]   = useState("newest");
  const [page,      setPage]      = useState(1);
  const [pageSize,  setPageSize]  = useState(10);
  const [viewModal,   setViewModal]   = useState<ViewModal>({ open: false, quote: null });
  const [deleteModal, setDeleteModal] = useState<DeleteModal>({ open: false, quote: null, deleting: false });
  const [toast,       setToast]       = useState<ToastState>({ visible: false, message: "", type: "success" });
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadQuotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchQuotes();
      setQuotes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadQuotes(); }, []);

  const showToast = (message: string, type: ToastState["type"] = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ visible: true, message, type });
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  };

  const sortOpt = SORT_OPTIONS.find((o) => o.value === sortKey) ?? SORT_OPTIONS[0];

  const filtered = useMemo(() => {
    let list = [...quotes];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        r.name.toLowerCase().includes(q) ||
        r.company.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        (r.enzyme ?? "").toLowerCase().includes(q) ||
        (r.city ?? "").toLowerCase().includes(q) ||
        (r.country ?? "").toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const va = String(a[sortOpt.field] ?? "");
      const vb = String(b[sortOpt.field] ?? "");
      const cmp = va.localeCompare(vb);
      return sortOpt.dir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [quotes, search, sortOpt]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageSlice  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => { setPage(1); }, [search, sortKey, pageSize]);

  const handleDelete = async () => {
    if (!deleteModal.quote) return;
    setDeleteModal((m) => ({ ...m, deleting: true }));
    try {
      await deleteQuote(deleteModal.quote.id);
      setQuotes((prev) => prev.filter((q) => q.id !== deleteModal.quote!.id));
      showToast(`Quote #${deleteModal.quote.id} deleted`, "success");
      setDeleteModal({ open: false, quote: null, deleting: false });
    } catch {
      showToast("Failed to delete quote. Please try again.", "error");
      setDeleteModal((m) => ({ ...m, deleting: false }));
    }
  };

  const stats = useMemo(() => ({
    total:      quotes.length,
    thisWeek:   quotes.filter((r) => isThisWeek(r.created_at)).length,
    withEnzyme: quotes.filter((r) => !!r.enzyme).length,
    countries:  new Set(quotes.map((r) => r.country).filter(Boolean)).size,
  }), [quotes]);

  const pageNums = (): (number | "…")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (safePage <= 4)   return [1, 2, 3, 4, 5, "…", totalPages];
    if (safePage >= totalPages - 3) return [1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "…", safePage - 1, safePage, safePage + 1, "…", totalPages];
  };

  const handleExport = () => {
    exportCSV(filtered);
    showToast(`Exported ${filtered.length} record${filtered.length !== 1 ? "s" : ""} to CSV`, "success");
  };

  if (loading) return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <LoadingState />
    </>
  );

  if (error) return <ErrorState message={error} onRetry={loadQuotes} />;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        body { margin:0; font-family:'DM Sans',sans-serif; }
        input::placeholder, select { color:var(--text3); }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.08); border-radius:4px; }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastIn     { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes modalIn     { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
        @keyframes spin        { to { transform: rotate(360deg); } }
        .qr-row      { transition:background 0.12s; }
        .qr-row:hover{ background:var(--row-hover)!important; }
        .qr-th       { user-select:none; cursor:pointer; }
        .qr-th:hover { color:var(--text)!important; }
        .qr-pg:hover:not(:disabled) { background:var(--or-dim)!important; color:var(--or)!important; border-color:rgba(191,61,12,0.3)!important; }
        @media (max-width:900px)  { .quote-stats { grid-template-columns: repeat(2,1fr) !important; } }
        @media (max-width:480px)  { .quote-header { display:block !important; } .quote-stats { grid-template-columns: 1fr !important; } }
      `}</style>

      {/* Toast */}
      {toast.visible && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 700, background: toast.type === "error" ? "#2e1a1a" : "#1a2e1a", border: `1px solid ${toast.type === "error" ? "rgba(224,82,82,0.25)" : "rgba(90,200,90,0.25)"}`, borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10, animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
          <span style={{ fontSize: 13, color: toast.type === "error" ? "#e09b9b" : "#9be09b" }}>
            {toast.type === "error" ? "✕" : "✓"}&nbsp;&nbsp;{toast.message}
          </span>
        </div>
      )}

      <ViewModal modal={viewModal} onClose={() => setViewModal({ open: false, quote: null })} />
      <DeleteConfirmModal
        modal={deleteModal}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ open: false, quote: null, deleting: false })}
      />

      <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", animation: "fadeSlideIn 0.3s ease" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }} className="quote-header">
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>Sales</div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>Quote Requests</h1>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text3)" }}>
                {stats.total} total · {stats.thisWeek} this week · {stats.withEnzyme} with enzyme
              </p>
            </div>
            <button
              onClick={handleExport}
              style={{ display: "flex", marginTop: 10, alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 9, border: "1px solid rgba(191,61,12,0.35)", background: "linear-gradient(135deg,#BF3D0C,#E8593C)", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", boxShadow: "0 2px 12px rgba(191,61,12,0.25)" }}
            >
              <span style={{ fontSize: 14 }}>↓</span> Export CSV
            </button>
          </div>

          {/* Stat cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }} className="quote-stats">
            {[
              { label: "Total Requests", value: stats.total,      color: "var(--text2)", sub: "all time" },
              { label: "This Week",      value: stats.thisWeek,   color: "#8ab4f8",      sub: "last 7 days" },
              { label: "With Enzyme",    value: stats.withEnzyme, color: "var(--or)",    sub: "specific enzyme mentioned" },
              { label: "Countries",      value: stats.countries,  color: "#7dd8a4",      sub: "unique locations" },
            ].map((s) => (
              <div key={s.label} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "16px 20px" }}>
                <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 26, fontWeight: 600, color: s.color, lineHeight: 1, marginBottom: 4 }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "var(--text3)" }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Table panel */}
          <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>

            {/* Toolbar */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
              {/* Search */}
              <div style={{ position: "relative", flex: "1 1 220px", minWidth: 160 }}>
                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--text3)", fontSize: 14, pointerEvents: "none" }}>⌕</span>
                <input
                  value={search}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                  placeholder="Search name, company, email, enzyme, location…"
                  style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 30px 8px 32px", fontSize: 12, color: "var(--text)", outline: "none", fontFamily: "'DM Sans',sans-serif", boxSizing: "border-box" }}
                />
                {search && (
                  <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: 14 }}>×</button>
                )}
              </div>

              {/* Sort */}
              <select
                value={sortKey}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setSortKey(e.target.value)}
                style={{ fontSize: 12, padding: "7px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "rgba(255,255,255,0.04)", color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", outline: "none" }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} style={{ background: "#1a1917", color: "#e8e6de" }}>{o.label}</option>
                ))}
              </select>

              <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: "auto", whiteSpace: "nowrap" }}>
                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                    {([
                      { field: null,         label: "#",        w: 60  },
                      { field: "name",       label: "Name",     w: "auto" },
                      { field: "company",    label: "Company",  w: "auto" },
                      { field: "email",      label: "Email",    w: "auto" },
                      { field: null,         label: "Phone",    w: 130 },
                      { field: null,         label: "Enzyme",   w: 140 },
                      { field: null,         label: "Location", w: 150 },
                      { field: "created_at", label: "Submitted",w: 110 },
                      { field: null,         label: "Actions",  w: 90  },
                    ] as { field: SortField | null; label: string; w: number | string }[]).map((col, i) => (
                      <th
                        key={i}
                        className={col.field ? "qr-th" : undefined}
                        onClick={col.field ? () => {
                          const opt = SORT_OPTIONS.find((o) => o.field === col.field && o.dir === (sortOpt.field === col.field && sortOpt.dir === "asc" ? "desc" : "asc"))
                               ?? SORT_OPTIONS.find((o) => o.field === col.field);
                          if (opt) setSortKey(opt.value);
                        } : undefined}
                        style={{ width: col.w, padding: "11px 14px", textAlign: "left", borderBottom: "1px solid var(--border)", fontSize: 10, fontWeight: 600, color: (col.field && sortOpt.field === col.field) ? "var(--or)" : "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", whiteSpace: "nowrap" }}
                      >
                        {col.label}
                        {col.field && <SortIcon active={sortOpt.field === col.field} dir={sortOpt.dir} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageSlice.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ padding: "56px 24px", textAlign: "center", color: "var(--text3)", fontSize: 14 }}>
                        {search ? "No matching requests found." : "No quote requests yet."}
                      </td>
                    </tr>
                  ) : pageSlice.map((r, idx) => (
                    <tr
                      key={r.id}
                      className="qr-row"
                      style={{
                        background: "transparent",
                        borderBottom: idx < pageSlice.length - 1 ? "1px solid var(--border)" : "none",
                        animation: `fadeSlideIn 0.2s ease ${idx * 0.025}s both`,
                      }}
                    >
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ fontSize: 11, color: "var(--text3)", fontFamily: "monospace" }}>#{r.id}</span>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>{highlight(r.name, search)}</div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontSize: 13, color: "var(--text2)" }}>{highlight(r.company, search)}</div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, color: "var(--text3)" }}>{highlight(r.email, search)}</div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ fontSize: 12, color: r.phone ? "var(--text2)" : "var(--text3)" }}>
                          {r.phone ?? "—"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <Pill value={r.enzyme} />
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ fontSize: 12, color: (r.city || r.country) ? "var(--text2)" : "var(--text3)" }}>
                          {[r.city, r.country].filter(Boolean).join(", ") || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>
                        {fmtDate(r.created_at)}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <IconBtn title="View details" onClick={() => setViewModal({ open: true, quote: r })} accent>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </IconBtn>
                          <a
                            href={`mailto:${r.email}?subject=Re: Your Quote Request — Aurel Global&body=Dear ${encodeURIComponent(r.name)},%0A%0A`}
                            target="_blank"
                            rel="noreferrer"
                            title="Reply by email"
                            style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text3)", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", flexShrink: 0, transition: "all 0.15s" }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.05)"; (e.currentTarget as HTMLAnchorElement).style.color = "var(--text2)"; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "transparent"; (e.currentTarget as HTMLAnchorElement).style.color = "var(--text3)"; }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                              <polyline points="22,6 12,13 2,6"/>
                            </svg>
                          </a>
                          <IconBtn title="Delete quote" onClick={() => setDeleteModal({ open: true, quote: r, deleting: false })} danger>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                              <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "var(--text3)" }}>Rows per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{ fontSize: 11, padding: "4px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "rgba(255,255,255,0.04)", color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", outline: "none" }}
                >
                  {PAGE_SIZES.map((n) => <option key={n} value={n} style={{ background: "#1a1917" }}>{n}</option>)}
                </select>
              </div>
              <span style={{ fontSize: 11, color: "var(--text3)" }}>
                {filtered.length === 0 ? "0" : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)}`} of {filtered.length}
              </span>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {[
                  { l: "«", a: () => setPage(1),                            d: safePage === 1 },
                  { l: "‹", a: () => setPage((p) => Math.max(1, p - 1)),   d: safePage === 1 },
                ].map((b) => (
                  <button key={b.l} className="qr-pg" onClick={b.a} disabled={b.d} style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: b.d ? "var(--text3)" : "var(--text2)", cursor: b.d ? "not-allowed" : "pointer", fontSize: 13, opacity: b.d ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>{b.l}</button>
                ))}
                {pageNums().map((n, i) =>
                  n === "…"
                    ? <span key={`e${i}`} style={{ width: 30, textAlign: "center", color: "var(--text3)", fontSize: 12 }}>…</span>
                    : (
                      <button key={n} className="qr-pg" onClick={() => setPage(n as number)} style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${safePage === n ? "rgba(191,61,12,0.35)" : "var(--border)"}`, background: safePage === n ? "var(--or-dim)" : "transparent", color: safePage === n ? "var(--or)" : "var(--text2)", cursor: "pointer", fontSize: 12, fontWeight: safePage === n ? 600 : 400, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>{n}</button>
                    )
                )}
                {[
                  { l: "›", a: () => setPage((p) => Math.min(totalPages, p + 1)), d: safePage === totalPages },
                  { l: "»", a: () => setPage(totalPages),                          d: safePage === totalPages },
                ].map((b) => (
                  <button key={b.l} className="qr-pg" onClick={b.a} disabled={b.d} style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: b.d ? "var(--text3)" : "var(--text2)", cursor: b.d ? "not-allowed" : "pointer", fontSize: 13, opacity: b.d ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>{b.l}</button>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
